import { InputNumber, Segmented, Select } from 'antd'
import { useEffect, useId, useRef, useState } from 'react'

import { cx } from '@/Ship/Helpers/cssClass'
import { FILTER_DEBOUNCE_MS } from '@/Ship/Templates/CrudResource/Components/filterActions'
import { MILEAGE_DEVIATION_OPTIONS } from '@/Containers/ShiftSection/ShiftContainer/Contracts/MileageDeviation'

import {
    MILEAGE_LIST_FILTERS,
    type MileageControlStatus,
    type MileageFilterState,
    type MileageListFilter,
    type MileageUnit,
} from '../Contracts/Report'
import { REPORT_OPTION_SOURCES } from '../Helpers/reportSources'
import { ReportEntitySelect } from './ReportEntitySelect'
import { Field, ReportPeriodField } from './ReportFilterFields'

import styles from './Report.module.css'

const UNIT_OPTIONS: { value: MileageUnit; label: string }[] = [
    { value: 'km', label: 'км' },
    { value: 'percent', label: '%' },
]

const STATUS_OPTIONS = MILEAGE_DEVIATION_OPTIONS.map(({ value, label }) => ({ value, label }))

interface MileageFiltersProps {
    value: MileageFilterState
    onChange: (value: MileageFilterState) => void
}

// Порожнє поле — без межі; від'ємне відхилення за модулем не буває
const bound = (next: number | string | null): number | null =>
    next === null || next === '' || Number.isNaN(Number(next)) ? null : Math.max(0, Number(next))

interface BoundInputProps {
    id: string
    ariaLabel: string
    placeholder: string
    unitLabel: string
    value: number | null
    onCommit: (value: number | null) => void
}

/**
 * Межа відхилення. Як текстові фільтри CRUD: застосовується після паузи в наборі (а не на кожну
 * цифру «2 → 20 → 200»), Enter чи вихід з поля — одразу, очищене поле знімає межу одразу.
 */
const BoundInput = ({ id, ariaLabel, placeholder, unitLabel, value, onCommit }: BoundInputProps) => {
    const [draft, setDraft] = useState(value)
    const [shown, setShown] = useState(value)
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Таймер застосовує межу до актуальних решти фільтрів, а не до тих, що були на початку набору
    const commitRef = useRef(onCommit)
    useEffect(() => {
        commitRef.current = onCommit
    })

    // Межу змінили ззовні (чіп, «Скинути все») — показуємо її, а відкладене застосування скасовуємо
    if (value !== shown) {
        setShown(value)
        setDraft(value)
    }
    useEffect(
        () => () => {
            if (timer.current) clearTimeout(timer.current)
        },
        [value]
    )

    const commit = (next: number | null) => {
        if (timer.current) clearTimeout(timer.current)
        timer.current = null
        if (next !== value) commitRef.current(next)
    }

    return (
        <InputNumber<number>
            id={id}
            aria-label={ariaLabel}
            placeholder={placeholder}
            min={0}
            decimalSeparator=","
            value={draft}
            onChange={(raw) => {
                const next = bound(raw)
                setDraft(next)
                if (next === null) {
                    commit(null)
                    return
                }
                if (timer.current) clearTimeout(timer.current)
                timer.current = setTimeout(() => commit(next), FILTER_DEBOUNCE_MS)
            }}
            onPressEnter={() => commit(draft)}
            onBlur={() => commit(draft)}
            suffix={unitLabel}
        />
    )
}

/**
 * Смуга фільтра «Контролю пробігу» (застосовується одразу): період за датою зміни (обов'язковий),
 * межі відхилення однієї зміни за модулем — у км або %, водій / ТЗ / перевізник ТЗ, статус контролю.
 */
export const MileageFilters = ({ value, onChange }: MileageFiltersProps) => {
    const baseId = useId()
    const unitLabel = value.unit === 'percent' ? '%' : 'км'

    const setList = (name: MileageListFilter, options: MileageFilterState['lists'][MileageListFilter]) =>
        onChange({ ...value, lists: { ...value.lists, [name]: options } })

    return (
        <div className={cx(styles, 'filters')}>
            <div className={cx(styles, 'filterRow')}>
                <ReportPeriodField
                    label="Період (дата зміни)"
                    value={value.period}
                    onChange={(period) => period && onChange({ ...value, period })}
                />
                <Field label="Відхилення зміни (за модулем)" wide>
                    <div className={cx(styles, 'bounds')}>
                        <Segmented<MileageUnit>
                            aria-label="Одиниця відхилення"
                            options={UNIT_OPTIONS}
                            value={value.unit}
                            onChange={(unit) => onChange({ ...value, unit })}
                        />
                        <BoundInput
                            id={`${baseId}-min`}
                            ariaLabel={`Відхилення більше ніж, ${unitLabel}`}
                            placeholder="більше ніж"
                            unitLabel={unitLabel}
                            value={value.deviationMin}
                            onCommit={(deviationMin) => onChange({ ...value, deviationMin })}
                        />
                        <BoundInput
                            id={`${baseId}-max`}
                            ariaLabel={`Відхилення менше ніж, ${unitLabel}`}
                            placeholder="менше ніж"
                            unitLabel={unitLabel}
                            value={value.deviationMax}
                            onCommit={(deviationMax) => onChange({ ...value, deviationMax })}
                        />
                    </div>
                </Field>
            </div>

            <div className={cx(styles, 'filterRow')}>
                {MILEAGE_LIST_FILTERS.map((name) => {
                    const id = `${baseId}-${name}`
                    return (
                        <Field key={name} label={REPORT_OPTION_SOURCES[name].label} htmlFor={id}>
                            <ReportEntitySelect
                                id={id}
                                source={REPORT_OPTION_SOURCES[name]}
                                value={value.lists[name]}
                                onChange={(options) => setList(name, options)}
                            />
                        </Field>
                    )
                })}
                <Field label="Статус контролю" htmlFor={`${baseId}-status`}>
                    <Select<MileageControlStatus[]>
                        id={`${baseId}-status`}
                        mode="multiple"
                        allowClear
                        placeholder="Усі"
                        options={STATUS_OPTIONS}
                        value={value.statuses}
                        onChange={(statuses) => onChange({ ...value, statuses })}
                    />
                </Field>
            </div>
        </div>
    )
}
